import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Play, Pause, SkipForward, SkipBack, RotateCcw, Eye } from 'lucide-react';

/**
 * BacktestReplayControls — candle-by-candle date replay
 *
 * Steps through every candle date (from candleDates prop).
 * On each step:
 *   - onReplayDateChange(dateStr)  → TradingChart slices candles up to this date
 *   - onTradeSelect(tradeIndex)    → highlights most recently entered trade
 *
 * Pass null to onReplayDateChange to restore full overview.
 */
export default function BacktestReplayControls({
  trades = [],
  candleDates = [],       // all candle date strings from TradingChart
  onReplayDateChange,
  onTradeSelect,
  colors,
}) {
  const [isPlaying, setIsPlaying]   = useState(false);
  const [stepIndex, setStepIndex]   = useState(null); // null = overview
  const [speed, setSpeed]           = useState(30);   // ms per candle
  const [jumpInput, setJumpInput]   = useState('');
  const autoPlayRef = useRef(null);

  // Restrict candle dates to the backtest range (first entry → last exit)
  const replayDates = useMemo(() => {
    if (!candleDates.length || !trades.length) return candleDates;
    const firstEntry = trades.map(t => t.entry_date).sort()[0];
    const lastExit   = trades.map(t => t.exit_date).sort().at(-1);
    return candleDates.filter(d => d >= firstEntry && d <= lastExit);
  }, [candleDates, trades]);

  // Unique years within replay range
  const years = useMemo(() => {
    const set = new Set();
    replayDates.forEach(d => set.add(d.slice(0, 4)));
    return Array.from(set).sort();
  }, [replayDates]);

  const currentDate = stepIndex !== null ? replayDates[stepIndex] : null;

  // Most recent trade whose entry_date <= currentDate
  const currentTradeIndex = useMemo(() => {
    if (currentDate === null) return null;
    let last = null;
    trades.forEach((t, i) => { if (t.entry_date <= currentDate) last = i; });
    return last;
  }, [currentDate, trades]);

  // Notify parent
  useEffect(() => { onReplayDateChange?.(currentDate); }, [currentDate, onReplayDateChange]);
  useEffect(() => { onTradeSelect?.(currentTradeIndex); }, [currentTradeIndex, onTradeSelect]);

  // Auto-play — uses requestAnimationFrame batching for very fast speeds
  useEffect(() => {
    if (!isPlaying || !replayDates.length) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      return;
    }
    autoPlayRef.current = setInterval(() => {
      setStepIndex(prev => {
        const start = prev === null ? 0 : prev;
        const next  = start + 1;
        if (next >= replayDates.length) { setIsPlaying(false); return prev; }
        return next;
      });
    }, speed);
    return () => clearInterval(autoPlayRef.current);
  }, [isPlaying, speed, replayDates.length]);

  const handlePlay     = () => { if (stepIndex === null) setStepIndex(0); setIsPlaying(p => !p); };
  const handleStep     = () => { setIsPlaying(false); setStepIndex(p => Math.min((p ?? -1) + 1, replayDates.length - 1)); };
  const handleBack     = () => { setIsPlaying(false); setStepIndex(p => p === null || p === 0 ? null : p - 1); };
  const handleReset    = () => { setIsPlaying(false); setStepIndex(0); };
  const handleOverview = () => { setIsPlaying(false); setStepIndex(null); };

  const handleYearJump = (year) => {
    const idx = replayDates.findIndex(d => d.startsWith(year));
    if (idx !== -1) { setStepIndex(idx); setIsPlaying(false); }
  };

  const handleTradeJump = (e) => {
    e.preventDefault();
    const n = parseInt(jumpInput, 10) - 1;
    if (!isNaN(n) && n >= 0 && n < trades.length) {
      const entryDate = trades[n]?.entry_date;
      const idx = replayDates.indexOf(entryDate);
      if (idx !== -1) { setStepIndex(idx); setIsPlaying(false); }
    }
    setJumpInput('');
  };

  if (!trades.length) return null;

  const currentTrade = currentTradeIndex !== null ? trades[currentTradeIndex] : null;
  const isWin        = (currentTrade?.pnl_pct || 0) >= 0;
  const pnlColor     = isWin ? colors.profit : colors.loss;
  const isOverview   = stepIndex === null;
  const atEnd        = stepIndex !== null && stepIndex >= replayDates.length - 1;

  return (
    <div className="rounded-lg p-2.5 space-y-2" style={{ backgroundColor: colors.inputBg }}>
      {/* Status bar */}
      <div className="flex items-center justify-between text-[13px]">
        <span style={{ color: colors.secondaryText }}>
          {isOverview
            ? `Overview — ${trades.length} trades · ${replayDates.length} candles`
            : `${currentDate}  (${stepIndex + 1} / ${replayDates.length})`}
        </span>
        {currentTrade && !isOverview && (
          <div className="flex gap-3">
            <span style={{ color: colors.secondaryText }}>
              T{(currentTradeIndex ?? 0) + 1}: {currentTrade.entry_date} → {currentTrade.exit_date}
            </span>
            {currentDate >= currentTrade.exit_date && (
              <>
                <span style={{ color: pnlColor, fontWeight: 'bold' }}>
                  {isWin ? '+' : ''}{(currentTrade.pnl_pct || 0).toFixed(2)}%
                </span>
                <span style={{
                  backgroundColor: `${currentTrade.exit_reason === 'SL' ? colors.loss : currentTrade.exit_reason === 'TP' ? colors.profit : colors.accent}20`,
                  color: currentTrade.exit_reason === 'SL' ? colors.loss : currentTrade.exit_reason === 'TP' ? colors.profit : colors.accent,
                  padding: '2px 6px', borderRadius: '4px',
                }}>{currentTrade.exit_reason}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Eye — back to overview */}
        <button onClick={handleOverview} title="Full overview"
          className="p-1 rounded"
          style={{ backgroundColor: isOverview ? colors.accent : colors.cardBg, color: isOverview ? '#fff' : colors.secondaryText }}>
          <Eye className="w-4 h-4" />
        </button>

        {/* Reset to first candle */}
        <button onClick={handleReset} title="Reset to start" className="app-card p-1 rounded"
          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.secondaryText }}>
          <RotateCcw className="w-4 h-4" />
        </button>

        {/* Back one candle */}
        <button onClick={handleBack} disabled={isOverview} title="Previous candle"
          className="app-card p-1 rounded"
          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: isOverview ? colors.border : colors.accent, opacity: isOverview ? 0.4 : 1 }}>
          <SkipBack className="w-4 h-4" />
        </button>

        {/* Play / Pause */}
        <button onClick={handlePlay} title={isPlaying ? 'Pause' : 'Play'} className="p-1 rounded"
          style={{ backgroundColor: colors.accent, color: '#fff' }}>
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>

        {/* Forward one candle */}
        <button onClick={handleStep} disabled={atEnd} title="Next candle"
          className="app-card p-1 rounded"
          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: atEnd ? colors.border : colors.accent, opacity: atEnd ? 0.4 : 1 }}>
          <SkipForward className="w-4 h-4" />
        </button>

        {/* Speed */}
        <select value={speed} onChange={e => setSpeed(parseInt(e.target.value))}
          className="text-[13px] px-2 py-1 rounded"
          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
          <option value={16}>Fast</option>
          <option value={50}>0.05s</option>
          <option value={100}>0.1s</option>
          <option value={300}>0.3s</option>
          <option value={800}>0.8s</option>
          <option value={1500}>1.5s</option>
        </select>

        {/* Year jump */}
        {years.length > 1 && (
          <select defaultValue=""
            onChange={e => { if (e.target.value) handleYearJump(e.target.value); e.target.value = ''; }}
            className="text-[13px] px-2 py-1 rounded"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
            <option value="" disabled>Jump year</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}

        {/* Trade # jump */}
        <form onSubmit={handleTradeJump} className="flex items-center gap-1">
          <input type="number" min={1} max={trades.length}
            value={jumpInput} onChange={e => setJumpInput(e.target.value)}
            placeholder={`#1–${trades.length}`}
            className="text-[13px] px-2 py-1 rounded w-20"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText, border: `1px solid ${colors.border}` }} />
          <button type="submit" className="app-card text-[13px] px-2 py-1 rounded"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.accent, border: `1px solid ${colors.border}` }}>
            Go
          </button>
        </form>
      </div>
    </div>
  );
}
